import { ButtonUtility } from "@components";
import { ArrowLeft, ArrowRight } from "@untitledui/icons";
import type { FC } from "react";
import type { CnhStatusCardProps } from "../types";
import { EmptyStateIllustration } from "./EmptyStateIllustration";

const CARD_CLASS =
	"flex h-auto w-full flex-col rounded-xl border border-primary bg-primary p-1.5 shadow-xs";
const CARD_TITLE_CLASS = "font-semibold text-lg text-primary leading-7";

export const CnhStatusCard: FC<CnhStatusCardProps> = ({
	title,
	count,
	descriptionIfEmpty,
	descriptionIfFilled,
	currentPage = 1,
	totalPages = 1,
	onPrevPage,
	onNextPage,
	items,
	onSelectDriver,
}) => {
	const handlePrev = () => currentPage > 1 && onPrevPage?.();
	const handleNext = () => currentPage < totalPages && onNextPage?.();

	const isPrevDisabled = currentPage <= 1;
	const isNextDisabled = currentPage >= totalPages;

	if (count === 0) {
		return (
			<div className={`${CARD_CLASS} items-center`}>
				<div className="flex h-full w-full flex-col items-start gap-16 p-5">
					<h3 className={CARD_TITLE_CLASS}>{title}</h3>

					<div className="flex w-full flex-col items-center gap-5 text-center">
						<EmptyStateIllustration label={descriptionIfEmpty} />
						<p className="w-full max-w-[280px] font-semibold text-tertiary text-xl">
							{descriptionIfEmpty}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`${CARD_CLASS} justify-between`}>
			<div className="flex flex-col items-start gap-2 p-6">
				<h3 className={CARD_TITLE_CLASS}>{title}</h3>
				<p className="text-md text-tertiary">
					{descriptionIfFilled?.(count) ?? `Você possui ${count} registro(s).`}
				</p>
			</div>

			{Boolean(items?.length) && (
				<ul className="mb-auto flex flex-col gap-3 p-6">
					{items?.map((driver) => (
						<li key={driver.id}>
							<button
								className="rounded-sm text-left text-secondary outline-focus-ring hover:text-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
								onClick={() => onSelectDriver?.(driver)}
								type="button"
							>
								{driver.name}
							</button>
						</li>
					))}
				</ul>
			)}

			{totalPages > 1 ? (
				<div className="flex items-center justify-between gap-6 border-primary border-t p-6 text-secondary text-sm">
					<ButtonUtility
						color="secondary"
						icon={ArrowLeft}
						isDisabled={isPrevDisabled}
						onClick={handlePrev}
						size="sm"
						tooltip="Página anterior"
					/>
					<span>
						Página {currentPage} de {totalPages}
					</span>
					<ButtonUtility
						color="secondary"
						icon={ArrowRight}
						isDisabled={isNextDisabled}
						onClick={handleNext}
						size="sm"
						tooltip="Próxima página"
					/>
				</div>
			) : null}
		</div>
	);
};
